import React, { useState } from 'react';
import { Download, FileText, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const serviceGuideInfo = {
  'residential-painting': {
    title: 'Interior & Exterior Painting Guide',
    description: 'Learn paint selection, surface prep, and pro tips for a flawless finish.'
  },
  'remodeling-renovations': {
    title: 'Kitchen & Bath Remodeling Roadmap',
    description: 'Plan your renovation with our step-by-step guide and budgeting tips.'
  },
  'drywall-repair': {
    title: 'Drywall Repair & Installation Guide',
    description: 'Expert advice on repairs, texture matching, and installation.'
  },
  'carpentry': {
    title: 'Custom Carpentry & Trim Guide',
    description: 'Discover trim options, wood selection, and design inspiration.'
  },
  'tile-installation': {
    title: 'Tile Selection & Installation Guide',
    description: 'Navigate tile types, layouts, and professional installation tips.'
  },
  'exterior-siding': {
    title: 'Exterior Siding Selection Guide',
    description: 'Compare siding materials and learn maintenance best practices.'
  },
  'deck-construction': {
    title: 'Deck Design & Construction Guide',
    description: 'Plan your perfect outdoor space with material comparisons and design ideas.'
  },
  'mold-remediation': {
    title: 'Mold Prevention & Remediation Guide',
    description: 'Understand mold risks, prevention, and professional remediation.'
  },
  'parking-lot-striping': {
    title: 'Parking Lot Maintenance Guide',
    description: 'ADA compliance requirements, striping best practices, and asphalt care tips.'
  },
  'brick-masonry': {
    title: 'Brick & Masonry Care Guide',
    description: 'Maintenance tips for brickwork, stonework, stucco, and mortar repairs.'
  },
  'roof-repairs': {
    title: 'Roof Maintenance & Leak Prevention Guide',
    description: 'Spot early warning signs, prevent water damage, and extend roof life.'
  }
};

const GuideDownloadCTA = ({ serviceId, className = '' }) => {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const guideInfo = serviceGuideInfo[serviceId] || {
    title: 'Home Improvement Guide',
    description: 'Get our comprehensive home improvement guide with expert tips.'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/guides/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          firstName: formData.firstName,
          service: serviceId,
          guideType: 'service'
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        toast.success('Your guide is ready!');
        window.open(`${process.env.REACT_APP_BACKEND_URL}${data.download_url}`, '_blank');
        setShowModal(false);
        setFormData({ firstName: '', email: '' });
      } else {
        throw new Error('Failed to process request');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* CTA Card */}
      <div className={`bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 border border-amber-200 ${className}`}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-neutral-900 mb-1">FREE: {guideInfo.title}</h3>
            <p className="text-sm text-neutral-600 mb-4">{guideInfo.description}</p>
            <Button 
              onClick={() => setShowModal(true)}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid={`guide-download-btn-${serviceId}`}
            >
              <Download className="w-4 h-4 mr-2" />
              Download Free Guide
            </Button>
          </div>
        </div>
      </div>

      {/* Download Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-1">Get Your Free Guide</h3>
              <p className="text-sm text-neutral-600">{guideInfo.title}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="guideFirstName" className="text-neutral-700 mb-1 block">First Name</Label>
                <Input 
                  id="guideFirstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="Your first name"
                  required
                  className="h-11"
                />
              </div>
              <div>
                <Label htmlFor="guideEmail" className="text-neutral-700 mb-1 block">Email Address</Label>
                <Input 
                  id="guideEmail"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  required
                  className="h-11"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-5"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Download Now'}
              </Button>
            </form>

            <p className="text-xs text-neutral-400 text-center mt-4">
              We&apos;ll also send helpful tips to your inbox. Unsubscribe anytime.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default GuideDownloadCTA;
